import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { useEditorPasteDetection } from '@confluence/editor-features/entry-points/useEditorPasteDetection';
import { useTitleGenerationStreaming } from '@confluence/editor-features/entry-points/useTitleGenerationStreaming';

interface EditorTitleAIGenerationOnPasteProps {
	contentId: string;
	cloudId: string;
	title: string | null;
	userOffline?: boolean;
	onTitleGenerated: (title: string) => void;
	onTitleGeneratingChange?: (isGenerating: boolean) => void;
	isEditorEmpty: boolean;
}

export const EditorTitleAIGenerationOnPaste: React.FC<EditorTitleAIGenerationOnPasteProps> = ({
	contentId,
	cloudId,
	title,
	userOffline,
	onTitleGenerated,
	onTitleGeneratingChange,
	isEditorEmpty,
}) => {
	const {
		shouldShowStreaming,
		documentContext,
		onStreamingComplete,
		onStreamingError,
		isTitleGenerating,
	} = useEditorPasteDetection({
		contentId,
		cloudId,
		title,
		onTitleChange: onTitleGenerated,
		userOffline,
		isEditorEmpty,
	});

	const { locale } = useIntl();

	const hasCompletedRef = useRef(false);
	const prevIsTitleGeneratingRef = useRef(isTitleGenerating);

	useEffect(() => {
		if (isTitleGenerating && !prevIsTitleGeneratingRef.current) {
			hasCompletedRef.current = false;
		}
		prevIsTitleGeneratingRef.current = isTitleGenerating;
	}, [isTitleGenerating]);

	const { content, responseState, error } = useTitleGenerationStreaming({
		contentId,
		cloudId,
		documentContext,
		locale,
		skip: !shouldShowStreaming,
		onTitleChange: (newTitle) => {
			if (!hasCompletedRef.current) {
				hasCompletedRef.current = true;
				onTitleGenerated(newTitle);
			}
		},
	});

	useEffect(() => {
		if (responseState === 'FinalResponse' && content) {
			onStreamingComplete();
		} else if (responseState === 'Error' && error) {
			onStreamingError();
		}
	}, [responseState, content, error, onStreamingComplete, onStreamingError]);

	useEffect(() => {
		onTitleGeneratingChange?.(isTitleGenerating);
	}, [isTitleGenerating, onTitleGeneratingChange]);

	return null;
};
EditorTitleAIGenerationOnPaste.displayName = 'EditorTitleAIGenerationOnPaste';
